import { useEffect, useState } from "react";
import {
  PaginationModel,
  SetPaginationModel,
  usePaginationModel,
} from "../hooks/usePaginationModel";

type PreferredConfigurationsProps = {
  pageSize: number;
  visibleColumns: string[];
};

// The minimum required shape for column options
type ColumnOption = {
  field: string;
  headerName?: string;
  value: string;
  label: string;
  [key: string]: unknown;
};

type WithPaginationAndColumnVisibilityProps = {
  visibleColumns: string[];
  setVisibleColumns: React.Dispatch<React.SetStateAction<string[]>>;
  paginationModel: PaginationModel;
  setPaginationModel: SetPaginationModel;
};

type ConfigOptions = {
  columnOptions: ColumnOption[];
  defaultHiddenColumns: string[];
  storageKey: string;
  defaultPageSize?: number;
};

const PaginationAndColumnVisibilityHoc = <P extends object>(
  WrappedComponent: React.ComponentType<
    P & WithPaginationAndColumnVisibilityProps
  >,
  config: ConfigOptions
) => {
  const {
    columnOptions,
    defaultHiddenColumns,
    storageKey,
    defaultPageSize = 10,
  } = config;

  const PreferredConfigurationsKey = "bytro-frontend-configuration" as const;

  // Helper function to ensure we always work with string arrays
  const normalizeColumns = (columns: unknown): string[] => {
    if (!Array.isArray(columns)) return [];
    return columns.map(String);
  };

  // Helper to safely parse JSON from localStorage
  const safeJsonParse = <T,>(value: string | null, fallback: T): T => {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch {
      return fallback;
    }
  };

  // Memoize the default visible columns calculation
  const getDefaultVisibleColumns = (
    allColumns: ColumnOption[],
    hiddenKeys: string[]
  ) =>
    allColumns
      .filter((col) => !hiddenKeys.includes(col.value))
      .map((col) => col.value);

  const getPreferredConfigurations = (
    columnKeyName: string,
    allColumnOptions: ColumnOption[],
    defaultHiddenKeys: string[]
  ): PreferredConfigurationsProps => {
    // Get defaults once
    const defaultConfig = {
      pageSize: defaultPageSize,
      visibleColumns: getDefaultVisibleColumns(
        allColumnOptions,
        defaultHiddenKeys
      ),
    };

    try {
      const raw = localStorage.getItem(PreferredConfigurationsKey);
      if (!raw) return defaultConfig;

      const parsed = safeJsonParse<Record<string, unknown>>(raw, {});

      // Get page size with validation
      const pageSize =
        typeof parsed.pageSize === "number" && parsed.pageSize > 0
          ? parsed.pageSize
          : defaultConfig.pageSize;

      // Get visible columns with validation
      const storedVisible = parsed[columnKeyName];
      const visibleColumns =
        Array.isArray(storedVisible) && storedVisible.length > 0
          ? storedVisible.filter(
              (col): col is string => typeof col === "string"
            )
          : defaultConfig.visibleColumns;

      return { pageSize, visibleColumns };
    } catch (error) {
      console.error("Error loading preferences:", error);
      return defaultConfig;
    }
  };

  const setPreferredConfigurations = (
    columnKeyName: string,
    updates: Partial<PreferredConfigurationsProps>
  ) => {
    try {
      const current = safeJsonParse<Record<string, unknown>>(
        localStorage.getItem(PreferredConfigurationsKey),
        {}
      );

      // Only update if we have valid updates
      if (updates.pageSize && updates.pageSize > 0) {
        current.pageSize = updates.pageSize;
      }

      if (Array.isArray(updates.visibleColumns)) {
        current[columnKeyName] = updates.visibleColumns;
      }

      localStorage.setItem(PreferredConfigurationsKey, JSON.stringify(current));
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  return function WithPaginationAndColumnVisibility(
    props: Omit<P, keyof WithPaginationAndColumnVisibilityProps>
  ) {
    const prefs = getPreferredConfigurations(
      storageKey,
      columnOptions,
      defaultHiddenColumns
    );

    const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
      normalizeColumns(prefs.visibleColumns)
    );

    // Use pagination hook with callback to persist page size changes
    const { paginationModel, setPaginationModel } = usePaginationModel({
      defaultPageSize: prefs.pageSize || defaultPageSize,
      onPageSizeChange: (pageSize) => {
        setPreferredConfigurations(storageKey, { pageSize });
      },
    });

    useEffect(() => {
      setPreferredConfigurations(storageKey, { visibleColumns });
    }, [visibleColumns]);

    return (
      <WrappedComponent
        {...(props as P)}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
      />
    );
  };
};

export default PaginationAndColumnVisibilityHoc;
